import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import styles from 'components/Order/OrderDetails/OrderDetails.module.css'
import { Loader } from 'components/Shared/Loader'
import {
  fetchOrderRating,
  getIsMerchantLimitExceeded,
  getOrderMailingHistory,
  OrderStatus,
  rateOrderByForwarder,
  sendForApproval,
  setOrderStatus,
  subscribeEmailSent,
  subscribeOrderActionsUpdated
} from 'services'
import { PrimaryButton } from 'components/Shared/PrimaryButton'
import HistoryTabs from 'components/Order/OrderDetails/HistoryTabs/HistoryTabs'
import { StatusBar } from 'components/Order/OrderDetails/StatusBar/StatusBar'
import { OrderStatusSelectField } from 'components/Order/OrderDetails/OrderStatusSelectField'
import { SuccessDialog } from 'components/Shared/Dialogs/SuccessDialog'
import { WarningDialog } from 'components/Shared/Dialogs/WarningDialog'
import { fetchOrder, fetchOrderToken, getOrderActions, getStatusBar } from 'services/OrderService'
import { useErrorHandling } from 'context/errorHandling'
import { useWebSocketConnection } from 'context'
import { HTTP_400_ERROR, HTTP_404_ERROR, HTTP_409_ERROR, HTTP_422_ERROR, HTTP_500_ERROR } from 'Constants'
import OrderDetailsHeader from 'components/Order/OrderDetails/OrderDetailsHeader'
import { OrderData } from 'components/Order/OrderDetails/OrderData'
import { Tooltip } from '@material-ui/core'
import {
  ORDER_APPROVE_FOR_FULFILLMENT,
  ORDER_EDIT,
  ORDER_HISTORY_READ,
  ORDER_HISTORY_WRITE,
  useAuth
} from 'context/auth'
import { StatusChangeDialog } from 'components/Order/OrderDetails/StatusChangeDialog'
import { Toast } from 'react-bootstrap'
import { useUserGroups } from 'context/userGroups'
import { useOrderForwardingSummaryPdf } from './useOrderForwardingSummaryPdf'
import { OrderForwardingSummaryPdfChip } from './OrderForwardingSummaryPdfChip'
import { OrderForwardingSummarySettingsDialog } from './OrderForwardingSummarySettingsDialog'
import { ForwarderOrderRatingsDialog } from 'components/OrderRating'
import { ReadonlyOrderRatingsDialog } from 'components/OrderRating/OrderRatingsDialog/ReadonlyOrderRatingsDialog'
import { orderRatingByClientFieldOptions, orderRatingByForwarderFieldOptions } from 'components/OrderRating/utils'
import ApiError from 'constants/ApiError'
import { ErrorDialog } from "../../Shared/Dialogs";
import { orderVerificationWarnings, orderVerificationWarningsInit } from "../utils";
import {
  CARRIER_VERIFICATION_REQUIRED,
  MISSING_OR_INACTIVE_POLICY
} from "../../../services/errorCodes";

export function OrdersDetails (props) {
  const [order, setOrder] = useState(null)
  const [successSendingToDriverDialogOpen, setSuccessSendingToDriverDialogOpen] = useState(false)
  const [successSendingForApprovalDialogOpen, setSuccessSendingForApprovalDialogOpen] = useState(false)
  const [successAcceptingForFulfillmentDialogOpen, setSuccessAcceptingForFulfillmentDialogOpen] = useState(false)
  const [warningSendingToDriverDialogOpen, setWarningSendingToDriverDialogOpen] = useState(false)
  const [warningSendingForApprovalDialogOpen, setWarningSendingForApprovalDialogOpen] = useState(false)
  const [warningAcceptingForFulfillmentDialogOpen, setWarningAcceptingForFulfillmentDialogOpen] = useState(false)
  const [sendOrderForwardingSummaryPdfDialogOpen, setSendOrderForwardingSummaryPdfDialogOpen] = useState(false)
  const [orderForwardingSummaryPdfSettingsDialogOpen, setOrderForwardingSummaryPdfSettingsDialogOpen] = useState(false)
  const [orderToken, setOrderToken] = useState(null)
  const [actions, setActions] = useState([])
  const [statuses, setStatuses] = useState([])
  const [mailingHistoryList, setMailingHistoryList] = useState()
  const [isMerchantLimitExceeded, setIsMerchantLimitExceeded] = useState(null)
  const [isMailAlreadySent, setIsMailAlreadySent] = useState(true)
  const [isRatingsDialogOpen, setIsRatingsDialogOpen] = useState(false)
  const [isRatingDialogOpen, setIsRatingDialogOpen] = useState(false)
  const [orderRating, setOrderRating] = useState(null)
  const { hasAnyPrivileges, globalConfig } = useAuth()
  const [client, setClient] = useState(null)
  const [carrier, setCarrier] = useState(null)
  const [warningVerificationFailedDialogOpen, setWarningVerificationFailedDialogOpen] = useState(false)
  const [verificationWarnings, setVerificationWarnings] = useState({ ...orderVerificationWarningsInit })

  const { handleError } = useErrorHandling()
  const { hasMainGroupById } = useUserGroups()

  const history = useHistory()
  const id = props.match.params.id
  const previewedAttachmentId = props.match.params.attachmentId

  const isWebSocketConnection = useWebSocketConnection()

  const { t } = useTranslation()

  const auth = useAuth()

  const {
    isOrderForwardingSummaryPdfReady,
    isOrderForwardingSummaryPdfLoading,
    orderForwardingSummaryPdfFilename,
    isOrderForwardingSummaryPdfSent,
    isOrderForwardingSummaryPdfSendInProgress,
    generateOrderForwardingSummaryPdf,
    downloadOrderForwardingSummaryPdf,
    sendOrderForwardingSummaryPdf,
    clearOrderForwardingSummaryPdf
  } = useOrderForwardingSummaryPdf(id)

  const fetchStatuses = useCallback(() => {
    getStatusBar(id)
      .then(response => setStatuses(response))
      .catch(handleError)
  }, [id, handleError])

  const fetchActions = useCallback(() => {
    getOrderActions(id)
      .then(response => setActions(response))
      .catch(handleError)
  }, [id, handleError])

  const fetchOrderMailingHistory = useCallback(() => {
    getOrderMailingHistory(id)
      .then(mails => {
        setMailingHistoryList(mails)
        const requestForApprovalMail = mails.find(mail => mail.emailType === 'REQUEST_FOR_APPROVAL')
        setIsMailAlreadySent(requestForApprovalMail?.sendStatus === 'SUCCESS')
      })
      .catch(handleError)
  }, [id, handleError, setIsMailAlreadySent])

  useEffect(() => {
    if (isWebSocketConnection) {
      return subscribeOrderActionsUpdated(id, () => {
        fetchActions()
        fetchStatuses()
        fetchOrder(id).then(newOrder => {
          setOrder(order => ({ ...order, status: newOrder.status }))
        })
      })
    }
  }, [isWebSocketConnection, id, fetchActions, fetchStatuses])

  useEffect(() => {
    if (isWebSocketConnection) {
      return subscribeEmailSent(id, () => {
        fetchOrderMailingHistory()
      })
    }
  }, [isWebSocketConnection, id, fetchOrderMailingHistory])

  useEffect(() => {
    if (!orderRating) {
      fetchOrderRating(id)
        .then(setOrderRating)
        .catch(handleError)
    }
  }, [id, orderRating, handleError])

  const setOrderIsMerchantLimitExceeded = useCallback((orderStatus) => {
    if (orderStatus === 'SAVED') {
      getIsMerchantLimitExceeded(id)
        .then(setIsMerchantLimitExceeded)
        .catch(handleError)
    } else {
      setIsMerchantLimitExceeded(false)
    }
  }, [id, handleError])

  useEffect(() => {
    if (order) {
      const forwarderRatingMissing = orderRating && !orderRating.forwarderOrderRating
      const responsibleUserLogged = auth.user && (auth.user.id === order.forwarderId || auth.user.id === order.dispatcherId)
      const orderFinished = order.status.status === OrderStatus.Finished
      if (orderFinished && forwarderRatingMissing && responsibleUserLogged) {
        setIsRatingsDialogOpen(true)
      }
    }
  }, [orderRating, auth, order])

  const handleStatusChanged = useCallback((newStatus) => {
    setOrder(order => ({ ...order, status: newStatus }))
    setOrderIsMerchantLimitExceeded(newStatus?.status)
  }, [setOrderIsMerchantLimitExceeded])

  useEffect(() => {
    fetchOrderToken(id).then(orderToken => {
      setOrderToken(orderToken)
    }).catch(() => setOrderToken(null))

    fetchOrder(id).then(response => {
      response.attachments = [
        ...response.attachments.filter(attachment => !attachment.verified),
        ...response.attachments.filter(attachment => attachment.verified)]
      setOrder(response)
      const orderStatus = response.status?.status
      setOrderIsMerchantLimitExceeded(orderStatus)
      return response
    }).catch(handleError)
    fetchStatuses()
    fetchActions()
    fetchOrderMailingHistory()
  }, [fetchActions, fetchStatuses, fetchOrderMailingHistory, id, handleError, setOrderIsMerchantLimitExceeded])

  const onSendingToDriverDialogClose = (dialogValues) => {
    if (dialogValues.confirmed) {
      const newStatus = { status: OrderStatus.SentToDriver }
      setOrderStatus(id, newStatus, dialogValues).then((response) => {
        setWarningSendingToDriverDialogOpen(false)
        setSuccessSendingToDriverDialogOpen(true)
        handleStatusChanged(response)
      }).catch(error => {
        setWarningSendingToDriverDialogOpen(false)
        handleError(error, {
          [HTTP_404_ERROR]: 'notifications.error.sentToDriver',
          [HTTP_422_ERROR]: 'notifications.error.sentToDriver',
          [HTTP_500_ERROR]: 'notifications.error.sentToDriver'
        })
      })
    } else {
      setWarningSendingToDriverDialogOpen(false)
    }
  }

  const onSendingForApprovalDialogClose = (isSentConfirmed) => {
    if (isSentConfirmed) {
      sendForApproval(id).then((response) => {
        setWarningSendingForApprovalDialogOpen(false)
        setSuccessSendingForApprovalDialogOpen(true)
        setIsMailAlreadySent(true)
      }).catch(error => {
        setWarningSendingForApprovalDialogOpen(false)
        handleError(error, {
          [HTTP_404_ERROR]: 'notifications.error.sentForApproval',
          [HTTP_422_ERROR]: 'notifications.error.sentForApproval',
          [HTTP_500_ERROR]: 'notifications.error.sentForApproval'
        })
      })
    } else {
      setWarningSendingForApprovalDialogOpen(false)
    }
  }

  const onAcceptingForFulfillmentDialogClose = (dialogValues) => {
    if (dialogValues.confirmed) {
      const newStatus = { status: OrderStatus.AcceptedForFulfillment }
      setOrderStatus(id, newStatus, dialogValues).then((response) => {
        setWarningAcceptingForFulfillmentDialogOpen(false)
        setSuccessAcceptingForFulfillmentDialogOpen(true)
        handleStatusChanged(response)
      }).catch(error => {
        setWarningAcceptingForFulfillmentDialogOpen(false)
        const errorCode = error?.response?.data?.errorCode
        handleError(error, {
          [HTTP_404_ERROR]: 'notifications.error.acceptForFulfillment',
          [HTTP_422_ERROR]: 'notifications.error.acceptForFulfillment',
          [HTTP_500_ERROR]: 'notifications.error.acceptForFulfillment',
          [HTTP_400_ERROR]: errorCode && [MISSING_OR_INACTIVE_POLICY].includes(errorCode) ? `notifications.error.${errorCode}` :
            'notifications.error.acceptForFulfillment',
          [HTTP_409_ERROR]: errorCode && [CARRIER_VERIFICATION_REQUIRED].includes(errorCode) ? `notifications.error.${errorCode}` :
            'notifications.error.acceptForFulfillment'
        })
      })

    } else {
      setWarningAcceptingForFulfillmentDialogOpen(false)
    }
  }

  const onSendOrderForwardingSummaryPdfDialogClose = (isSendConfirmed) => {
    if (isSendConfirmed) {
      sendOrderForwardingSummaryPdf()
    }
    setSendOrderForwardingSummaryPdfDialogOpen(false)
  }

  const handleForwardingSummarySettingsDialogClose = (settings) => {
    if (settings) {
      generateOrderForwardingSummaryPdf(settings)
    }
    setOrderForwardingSummaryPdfSettingsDialogOpen(false)
  }

  const editOrder = () => {
    history.push(`/orders/edit/${id}`)
  }

  function updateAttachment (attachment) {
    setOrder({ ...order, attachments: order.attachments.map(a => a.id === attachment.id ? attachment : a) })
  }

  function removeAttachment (removedAttachment) {
    setOrder({ ...order, attachments: order.attachments.filter(attachment => attachment.id !== removedAttachment.id) })
    fetchActions()
  }

  function handleForwarderSubmit (values) {
    if (values) {
      rateOrderByForwarder(id, { ...values })
        .then(() => setOrderRating(null))
        .catch(error => {
          setOrderRating(null)
          handleError(error, {
            [HTTP_422_ERROR]: error?.response?.data?.errorCode === 'ORDER_ALREADY_RATED' ?
              'notifications.error.orderAlreadyRated' : ApiError.UnprocessableEntity
          })
        })
    }
    setIsRatingsDialogOpen(false)
  }

  useEffect(() => {
    setVerificationWarnings(orderVerificationWarnings(client, carrier, order?.highValue, order?.loadingDeliveryPoints, order?.unloadingDeliveryPoints, globalConfig?.carrierVerificationValidityAfterDischarge))
  }, [client, carrier, order, setVerificationWarnings, globalConfig])

  function canBeAcceptedForFulfilment () {
    if (verificationWarnings.notAllData) return
    if (Object.values(verificationWarnings).some(item => item === true)) {
      setWarningVerificationFailedDialogOpen(true)
    } else {
      setWarningAcceptingForFulfillmentDialogOpen(true)
    }
  }

  const hasEditAuthority = hasAnyPrivileges([ORDER_EDIT]) && hasMainGroupById(order?.groupId)

  function ApprovalButton () {
    return isMailAlreadySent ?
      <Tooltip title={(t('order.details.alreadySentForApprovalTooltip'))} arrow placement={'top'}>
        <div>
          <PrimaryButton
            onClick={() => setWarningSendingForApprovalDialogOpen(true)}
            label={t('order.details.alreadySentForApproval')}
            disabled/>
        </div>
      </Tooltip> :
      <PrimaryButton
        onClick={() => setWarningSendingForApprovalDialogOpen(true)}
        label={t('order.details.sendForApproval')}/>
  }

  const isOrderAcceptedForFulfillment = () => ![OrderStatus.Saved, OrderStatus.Cancelled].includes(order.status.status)

  return (order ? <div className={styles.orderDetailsWrapper}>
      <StatusChangeDialog open={warningSendingToDriverDialogOpen}
                          order={order}
                          newStatus={{
                            status: OrderStatus.SentToDriver,
                            clientNotified: false
                          }}
                          onClose={(result) => {
                            onSendingToDriverDialogClose(result)
                          }}
      />
      <SuccessDialog open={successSendingToDriverDialogOpen}
                     onClose={() => {
                       setSuccessSendingToDriverDialogOpen(false)
                       history.push('/orders')
                     }} content={'notifications.success.sentToDriver'}
      />
      <WarningDialog open={warningSendingForApprovalDialogOpen}
                     onClose={onSendingForApprovalDialogClose}
                     title={'notifications.warnings.sendForApprovalTitle'}
                     content={'notifications.warnings.sendForApproval'}
      />
      <SuccessDialog open={successSendingForApprovalDialogOpen}
                     onClose={() => {
                       setSuccessSendingForApprovalDialogOpen(false)
                     }} content={'notifications.success.sentForApproval'}
      />
      <StatusChangeDialog open={warningAcceptingForFulfillmentDialogOpen}
                          order={order}
                          newStatus={{
                            status: OrderStatus.AcceptedForFulfillment,
                            clientNotified: false
                          }}
                          onClose={(result) => {
                            onAcceptingForFulfillmentDialogClose(result)
                          }}
      />
      <WarningDialog open={sendOrderForwardingSummaryPdfDialogOpen}
                     onClose={onSendOrderForwardingSummaryPdfDialogClose}
                     title="order.details.orderForwardingSummaryPdf.sendDialog.title"
                     content="order.details.orderForwardingSummaryPdf.sendDialog.content"/>
      <OrderForwardingSummarySettingsDialog open={orderForwardingSummaryPdfSettingsDialogOpen}
                                            onClose={handleForwardingSummarySettingsDialogClose}/>
      <ErrorDialog open={warningVerificationFailedDialogOpen}
                   onClose={() => {
                     setWarningVerificationFailedDialogOpen(false)
                   }}
                   content={t('order.details.carrierVerificationFailed')}
      />
      <Toast onClose={() => {
        setSuccessAcceptingForFulfillmentDialogOpen(false)
      }}
             show={successAcceptingForFulfillmentDialogOpen}
             delay={1000}
             autohide>
        <SuccessDialog open={successAcceptingForFulfillmentDialogOpen}
                       onClose={() => {
                         setSuccessAcceptingForFulfillmentDialogOpen(false)
                       }} content={'notifications.success.acceptForFulfillment'}
        />
      </Toast>
      <ForwarderOrderRatingsDialog onSubmit={handleForwarderSubmit}
                                   isOpen={isRatingsDialogOpen}
                                   closeable={false}/>
      <ReadonlyOrderRatingsDialog onClose={() => setIsRatingDialogOpen(false)}
                                  isOpen={isRatingDialogOpen}
                                  values={orderRating ? {
                                    ...orderRating.clientOrderRating,
                                    ...orderRating.forwarderOrderRating
                                  } : {}}
                                  ratingFields={[...orderRatingByClientFieldOptions, ...orderRatingByForwarderFieldOptions]}/>
      <OrderDetailsHeader order={order}
                          isMerchantLimitExceeded={isMerchantLimitExceeded}
                          verificationWarnings={verificationWarnings}
                          carrier={carrier}>
        <OrderStatusSelectField
          order={order}
          onStatusChange={handleStatusChanged}
          actions={actions}
          disabled={!hasEditAuthority}
          isMerchantLimitExceeded={isMerchantLimitExceeded}
          verificationWarnings={verificationWarnings}
        /></OrderDetailsHeader>
      <StatusBar statuses={statuses}/>
      {hasAnyPrivileges([ORDER_HISTORY_READ]) &&
        <HistoryTabs order={order}
                     actions={actions}
                     mailingHistoryList={mailingHistoryList}
                     refreshActions={fetchActions}
                     refreshStatuses={fetchStatuses}
                     onStatusChanged={handleStatusChanged}
                     disableEdit={!hasAnyPrivileges([ORDER_HISTORY_WRITE])}
        />
      }
      <div className={styles.divider}/>
      <div
        className={classNames(styles.detailsRow, styles.orderDetailsHeader)}>
        <div className={styles.orderDetailsTitle}>
          <h1>{t('order.details.header')}</h1>
        </div>
        <div className={styles.buttonsBox}>
          {!isOrderForwardingSummaryPdfReady && isOrderAcceptedForFulfillment() && (
            <PrimaryButton
              onClick={() => setOrderForwardingSummaryPdfSettingsDialogOpen(true)}
              label={t(
                'order.details.orderForwardingSummaryPdf.generateButton')}
              disabled={isOrderForwardingSummaryPdfLoading}/>
          )}
          {isOrderForwardingSummaryPdfReady && (
            <OrderForwardingSummaryPdfChip
              filename={orderForwardingSummaryPdfFilename}
              isSendMailDone={isOrderForwardingSummaryPdfSent}
              isSentMailInProgress={isOrderForwardingSummaryPdfSendInProgress}
              onDownloadClick={downloadOrderForwardingSummaryPdf}
              onSendMailClick={() => setSendOrderForwardingSummaryPdfDialogOpen(true)}
              onDeleteClick={clearOrderForwardingSummaryPdf}
            />
          )}
          {isMerchantLimitExceeded && <ApprovalButton/>}
          {order.status?.status === 'SAVED' &&
            !isMerchantLimitExceeded && (
              <PrimaryButton
                onClick={canBeAcceptedForFulfilment}
                label={t('order.details.acceptOrderForFulfillment')}/>
            )}
          {(orderRating?.clientOrderRating || orderRating?.forwarderOrderRating) && (
            <PrimaryButton
              onClick={() => setIsRatingDialogOpen(true)}
              label={t('order.details.showClientRating')}/>
          )}
          <PrimaryButton
            onClick={editOrder}
            label={t('order.details.editOrder')}
            disabled={!hasEditAuthority}/>
          {order.deliveryMethod.internal && order.status?.status !== 'SAVED' &&
            (
              <PrimaryButton
                onClick={() => setWarningSendingToDriverDialogOpen(true)}
                label={t('order.details.sendToDriver')}
                disabled={!hasEditAuthority}/>
            )}
          {orderToken && (
            <PrimaryButton
              onClick={() => history.push({
                pathname: '/contractor-view/' + orderToken,
                search: '?rt=1' // query param to show order rate dialog for contractors only, not forwarders
              })}
              label={t('order.details.toContractorView')}/>
          )}
        </div>
      </div>
      <OrderData order={order}
                 attachmentsPreview={{
                   id: previewedAttachmentId,
                   updateAttachment,
                   removeAttachment
                 }}
                 onClientDetails={setClient}
                 onCarrierDetails={setCarrier}
                 verificationWarnings={verificationWarnings}
      />
    </div> :
    <Loader/>)
}

